"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.instagramSearch = exports.instagramSearchResultSchema = exports.instagramPlaceResultSchema = exports.instagramHashtagResultSchema = exports.instagramPostResultSchema = exports.instagramUserResultSchema = exports.instagramSearchArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
exports.instagramSearchArgsSchema = zod_1.z.object({
    query: zod_1.z.string().describe('The search query for Instagram'),
    limit: zod_1.z.number().min(1).max(100).describe('Number of results to fetch (max 100)'),
    searchType: zod_1.z
        .enum(['hashtag', 'user', 'place'])
        .default('user')
        .describe('Type of search to perform: user for profiles, hashtag for hashtag content, place for location-tagged content'),
    resultsType: zod_1.z
        .enum(['posts', 'details', 'mentions', 'stories'])
        .default('details')
        .describe('Type of results to return. Mentions and stories are only available for user search.'),
    emitMode: common_1.emitModeSchema.default('single').describe('The mode to emit the results in.'),
    options: zod_1.z
        .object({
        failIfEmpty: zod_1.z
            .boolean()
            .default(true)
            .describe('Fail if the search returns no results. Default to true.'),
    })
        .describe('Search options'),
});
var instagramMusicInfoSchema = zod_1.z.object({
    artistName: zod_1.z.string().nullable(),
    songName: zod_1.z.string().nullable(),
    usesOriginalAudio: zod_1.z.boolean().nullable(),
    audioId: zod_1.z.string().nullable(),
});
var instagramPostSchema = zod_1.z.object({
    url: zod_1.z.string(),
    caption: zod_1.z.string(),
    hashtags: zod_1.z.array(zod_1.z.string()),
    mentions: zod_1.z.array(zod_1.z.string()),
    likesCount: zod_1.z.number(),
    commentsCount: zod_1.z.number(),
    timestamp: zod_1.z.string(),
    location: zod_1.z.string().nullable(),
    owners: zod_1.z.array(zod_1.z.string()),
    type: zod_1.z.enum(['Image', 'Video', 'Sidecar']),
    media: zod_1.z.array(zod_1.z.object({
        imageUrl: zod_1.z.string(),
        type: zod_1.z.enum(['Image', 'Video']),
        height: zod_1.z.number(),
        width: zod_1.z.number(),
        alt: zod_1.z.string(),
        videoUrl: zod_1.z.string().nullable(),
        videoPlayCount: zod_1.z.number().nullable(),
        videoDuration: zod_1.z.number().nullable(),
        url: zod_1.z.string(),
    })),
    musicInfo: instagramMusicInfoSchema.nullable().optional(),
});
var instagramUserSchema = zod_1.z.object({
    username: zod_1.z.string(),
    fullName: zod_1.z.string().nullable(),
    profilePicUrl: zod_1.z.string().nullable(),
    isVerified: zod_1.z.boolean().nullable(),
    isPrivate: zod_1.z.boolean(),
    biography: zod_1.z.string().nullable(),
    followerCount: zod_1.z.number().nullable(),
    followingCount: zod_1.z.number().nullable(),
    postCount: zod_1.z.number().nullable(),
    latestPosts: zod_1.z.array(instagramPostSchema).nullable(),
});
var instagramHashtagSchema = zod_1.z.object({
    hashtag: zod_1.z.string(),
    postsCount: zod_1.z.number(),
    postsPerDay: zod_1.z.string(),
    latestPosts: zod_1.z.array(instagramPostSchema),
    topPosts: zod_1.z.array(instagramPostSchema),
    url: zod_1.z.string(),
    relatedHashtags: zod_1.z.array(zod_1.z.object({
        hashtag: zod_1.z.string(),
        info: zod_1.z.string(),
    })),
});
var instagramPlaceSchema = zod_1.z.object({
    name: zod_1.z.string(),
    category: zod_1.z.string(),
    url: zod_1.z.string(),
    postsCount: zod_1.z.number(),
    location: zod_1.z.object({
        lat: zod_1.z.number().nullable(),
        lng: zod_1.z.number().nullable(),
        name: zod_1.z.string(),
        address: zod_1.z.string().nullable(),
        city: zod_1.z.string().nullable(),
        zip: zod_1.z.string().nullable(),
    }),
    business: zod_1.z
        .object({
        username: zod_1.z.string(),
        profilePicUrl: zod_1.z.string(),
        phone: zod_1.z.string().nullable(),
    })
        .nullable(),
    posts: zod_1.z.array(instagramPostSchema),
});
// Individual result schemas
exports.instagramUserResultSchema = zod_1.z.array(instagramUserSchema);
exports.instagramPostResultSchema = zod_1.z.array(instagramPostSchema);
exports.instagramHashtagResultSchema = zod_1.z.array(instagramHashtagSchema);
exports.instagramPlaceResultSchema = zod_1.z.array(instagramPlaceSchema);
// Union schema for backward compatibility
exports.instagramSearchResultSchema = zod_1.z.array(zod_1.z.union([instagramUserSchema, instagramPostSchema, instagramHashtagSchema, instagramPlaceSchema]));
exports.instagramSearch = (0, common_1.createEmitModeToolMetadata)({
    id: 'instagramSearch',
    displayName: 'Instagram Search',
    displayDescription: 'Search for Instagram users, posts, hashtags, and places. Returns different data types based on search type.',
    category: 'search',
    color: common_1.BRAND_COLORS.INSTAGRAM,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.INSTAGRAM),
    icon: 'default',
    argsSchema: exports.instagramSearchArgsSchema,
}, {
    dependencies: ['resultsType', 'searchType'],
    variants: [
        // When explicitly requesting posts
        {
            when: { resultsType: 'posts' },
            schema: exports.instagramPostResultSchema,
            description: 'Returns Instagram post data when resultsType is posts',
        },
        // When requesting details, return the appropriate metadata type based on search type
        {
            when: { resultsType: 'details', searchType: 'hashtag' },
            schema: exports.instagramHashtagResultSchema,
            description: 'Returns hashtag metadata when requesting hashtag details',
        },
        {
            when: { resultsType: 'details', searchType: 'place' },
            schema: exports.instagramPlaceResultSchema,
            description: 'Returns place metadata when requesting place details',
        },
        {
            when: { resultsType: 'details', searchType: 'user' },
            schema: exports.instagramUserResultSchema,
            description: 'Returns user profile when requesting user details',
        },
        // Mentions and stories are only available for user search and return posts
        {
            when: { resultsType: 'mentions', searchType: 'user' },
            schema: exports.instagramPostResultSchema,
            description: 'Returns Instagram post data with mentions',
        },
        {
            when: { resultsType: 'stories', searchType: 'user' },
            schema: exports.instagramPostResultSchema,
            description: 'Returns Instagram stories as post data',
        },
    ],
    defaultSchema: exports.instagramSearchResultSchema,
});
